import { useMemo } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { abrirCotizacionPDF } from "../../../services/backend/cotizacionService";
import { CLP_FORMATTER } from "../../../utils/currency";
import { getCompany } from "../../../config/companyConfig";

const buildQuickAccessLinks = () => [
  { id: "tracking", label: "Seguimiento despacho", icon: "pi pi-truck", url: getCompany().trackingUrl },
  { id: "retiro", label: "Puntos de retiro", icon: "pi pi-map-marker", url: "/info/contactos-venta" },
  { id: "pickup", label: "Condiciones de despacho y retiro", icon: "pi pi-book", url: "/info/condiciones-de-despacho-y-retiro" },
];

const PurchaseHistory = ({
  purchasesFilters,
  purchaseHistory,
  historyNotice,
  isLoadingHistory,
  onUpdateFilter,
  onLoadHistory,
}) => {
  const opcionesEstado = useMemo(() => {
    const estados = [...new Set(purchaseHistory.map((item) => item.estado).filter(Boolean))];
    return [{ label: "Todos", value: null }, ...estados.map((e) => ({ label: e, value: e }))];
  }, [purchaseHistory]);

  const opcionesTipoEntrega = useMemo(() => {
    const tipos = [...new Set(purchaseHistory.map((item) => item.condicion).filter(Boolean))];
    return [{ label: "Todos", value: null }, ...tipos.map((t) => ({ label: t, value: t }))];
  }, [purchaseHistory]);

  const noHayDatos = !purchaseHistory || purchaseHistory.length === 0;

  const NumberFilter = (options) => (
    <InputText
      type="number"
      value={options.value || ""}
      onChange={(e) => options.filterApplyCallback(e.target.value)}
      placeholder="Buscar..."
      disabled={noHayDatos}
      className="p-column-filter p-inputtext-sm"
    />
  );

  const DateFilter = (options) => {
    const toFilterFormat = (isoValue) => {
      if (!isoValue) return "";
      const [year, month, day] = isoValue.split("-");
      return `${day}/${month}/${year}`;
    };
    const toInputFormat = (filterValue) => {
      if (!filterValue) return "";
      const [day, month, year] = String(filterValue).split("/");
      return `${year}-${month}-${day}`;
    };
    return (
      <InputText
        type="date"
        value={toInputFormat(options.value)}
        onChange={(e) => options.filterApplyCallback(toFilterFormat(e.target.value))}
        disabled={noHayDatos}
        className="p-column-filter p-inputtext-sm"
      />
    );
  };

  const TipoEntregaFilter = (options) => (
    <Dropdown
      value={options.value}
      options={opcionesTipoEntrega}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="Todos"
      showClear
      disabled={noHayDatos}
      className="p-column-filter"
    />
  );

  const EstadoFilter = (options) => (
    <Dropdown
      value={options.value}
      options={opcionesEstado}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="Todos"
      showClear
      disabled={noHayDatos}
      className="p-column-filter"
    />
  );

  const QUICK_ACCESS_LINKS = buildQuickAccessLinks();
  const downloadBodyTemplate = (item) => (
    <Button
      outlined
      severity="info"
      icon="pi pi-download"
      size="small"
      onClick={() => abrirCotizacionPDF(item.id || item.folio)}
      disabled={!String(item.id || item.folio || "").trim()}
    />
  );

  return (
    <div className="flex flex-column gap-3">
      <div className="grid">
        {QUICK_ACCESS_LINKS.map((link) => (
          <div key={link.id} className="col-12 md:col-4">
            <Card className="cursor-pointer" onClick={() => window.open(link.url, "_blank", "noopener,noreferrer")}>
              <div className="flex align-items-center gap-3">
                <i className={`${link.icon} text-xl`} style={{ color: "var(--primary-color)" }} />
                <div>
                  <strong className="admin-text-base">{link.label}</strong>
                  <p className="m-0 admin-text-sm" style={{ color: "var(--text-color-secondary)" }}>Abrir en nueva ventana</p>
                </div>
                <i className="pi pi-external-link ml-auto" style={{ color: "var(--text-color-secondary)" }} />
              </div>
            </Card>
          </div>
        ))}
      </div>

      <Card title="Historial de compras">
        <div className="flex align-items-end gap-3 flex-wrap">
          <div className="flex flex-column gap-1">
            <label htmlFor="purchases-from" className="font-bold admin-text-sm">Desde</label>
            <Calendar inputId="purchases-from" value={purchasesFilters.desde} onChange={(e) => onUpdateFilter("desde", e.value)} showIcon dateFormat="dd/mm/yy" />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="purchases-to" className="font-bold admin-text-sm">Hasta</label>
            <Calendar inputId="purchases-to" value={purchasesFilters.hasta} onChange={(e) => onUpdateFilter("hasta", e.value)} showIcon dateFormat="dd/mm/yy" />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="purchases-nro" className="font-bold admin-text-sm">N° PV o Entrega</label>
            <InputText id="purchases-nro" value={purchasesFilters.nroPV || ""} onChange={(e) => onUpdateFilter("nroPV", e.target.value)} placeholder="Buscar..." />
          </div>
          <Button label={isLoadingHistory ? "Cargando..." : "Aplicar"} size="medium" severity="success" icon="pi pi-filter" onClick={onLoadHistory} disabled={isLoadingHistory} />
        </div>
      </Card>

      <Card>
        {historyNotice ? <p className="mb-2 admin-text-sm">{historyNotice}</p> : null}
        <DataTable
          value={purchaseHistory}
          emptyMessage="No hay documentos"
          stripedRows
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="Mostrando {first} - {last} de {totalRecords}"
          responsiveLayout="scroll"
          filterDisplay="row"
        >
          <Column field="folio" header="N° PV / Entrega" body={(item) => item.folio || item.id || "-"} sortable filter filterElement={NumberFilter} showFilterMenu={false} style={{ width: "20%" }} />
          <Column field="fechaf" header="Fecha" body={(item) => item.fechaf || "-"} sortable filter filterElement={DateFilter} showFilterMenu={false} style={{ width: "15%" }} />
          <Column field="condicion" header="Tipo entrega" body={(item) => item.condicion || "-"} sortable filter filterElement={TipoEntregaFilter} showFilterMenu={false} style={{ width: "20%" }} />
          <Column field="estado" header="Estado" body={(item) => <Tag value={item.estado || "-"} />} sortable filter filterElement={EstadoFilter} showFilterMenu={false} style={{ width: "20%" }} />
          <Column field="total" header="Total" body={(item) => Number.isFinite(Number(item.total)) ? CLP_FORMATTER.format(Number(item.total)) : "-"} sortable filter filterElement={NumberFilter} showFilterMenu={false} style={{ width: "15%" }} />
          <Column body={downloadBodyTemplate} style={{ width: "120px" }} />
        </DataTable>
      </Card>
    </div>
  );
};

export default PurchaseHistory;
